// Document Module Configuration - UNIFIED APPROACH
// Manages document modules for QMS (see frontend/src/config/documentModuleCatalog.js)
// Based on Prisma DocumentModule model schema

import { createTrackingConfig } from '../../../components/generic/labels';

export const documentModuleConfig = {
  entityName: 'DocumentModule',
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/document-module',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
    },
    lookupEndpoints: {
      // Document modules typically don't have lookup dependencies
    }
  },
  
  // UI Configuration
  ui: {
    drawerWidth: '600px',
    defaultPageSize: 15,
    pageSizeOptions: [15, 25, 50, 100],
    defaultTableDensity: 'normal',
    requiredFieldStyle: 'badge'
  },
  
  // Service configuration
  service: {
    name: 'documentModuleService',
    methods: {
      getAll: 'getAllDocumentModules',
      getById: 'getDocumentModuleById', 
      create: 'createDocumentModule',
      update: 'updateDocumentModule',
      delete: 'deleteDocumentModule',
      validate: 'validateData'
    }
  },

  // UNIFIED FIELD CONFIGURATION
  fields: [
    // Basic Information
    { 
      name: 'id', 
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    { 
      name: 'name', 
      label: 'Module Name', 
      type: 'text', 
      required: true,
      table: { header: 'Module Name', width: '250px', sortable: true },
      validation: { 
        required: true, 
        minLength: 2, 
        maxLength: 100,
        message: 'Module name is required and must be between 2 and 100 characters'
      }
    },
    { 
      name: 'description', 
      label: 'Description', 
      type: 'textarea', 
      required: false,
      table: { header: 'Description', width: '300px', sortable: false },
      validation: { 
        required: false, 
        maxLength: 500 
      }
    },
    // User Relations (hidden, used for tracking)

    { 
      name: 'createdBy', 
      label: 'Created By', 
      type: 'text', 
      readOnly: true,
      table: false, // Don't show in main table
      validation: { required: false }
    },

    { 
      name: 'updatedBy', 
      label: 'Updated By', 
      type: 'text', 
      readOnly: true,
      table: false, // Don't show in main table
      validation: { required: false }
    },

    // System Fields (read-only)
    { 
      name: 'createdAt', 
      label: 'Created Date', 
      type: 'date', 
      readOnly: true,
      table: false,
      validation: { required: false }
    },
    { 
      name: 'updatedAt', 
      label: 'Updated Date', 
      type: 'date', 
      readOnly: true,
      table: false,
      validation: { required: false }
    }
  ],

  // Table Configuration - Dynamic getter that generates columns from fields
  get tableColumns() {
    return this.fields
      .filter(field => field.table !== false && !field.hidden)
      .map(field => ({
        key: field.name,
        header: field.table?.header || field.label,
        width: field.table?.width || 'auto',
        sortable: field.table?.sortable !== false,
        displayField: field.table?.displayField || field.name,
        cellRenderer: field.table?.cellRenderer || 'default'
      }));
  },

  // Filter Configuration
  filterableFields: ['name'],

  // Filter Groups for Basic/Advanced tabs
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic Filters',
      fields: ['name']
    },
  ],

  // Custom Sort Orders
  customSortOrders: {},

  // Messages Configuration
  messages: {
    loading: 'Loading document modules...',
    noData: 'No document modules found. Click "Add Module" to create your first module.',
    loadError: 'Failed to load document modules',
    saveSuccess: 'Document module saved successfully!',
    saveError: 'Failed to save document module',
    deleteSuccess: 'Document module deleted successfully!',
    deleteError: 'Failed to delete document module',
    deleteConfirm: 'Are you sure you want to delete this document module?'
  },

  // Form Layout Configuration
  layout: [
    {
      title: 'Basic Information',
      icon: '📝',
      fields: ['name', 'description']
    }
  ],

  // Tracking Section Configuration
  trackingSection: createTrackingConfig('DocumentModule'),

  // Labels Configuration
  labels: {
    // Using predefined labels + custom overrides
    singular: 'Document Module',
    plural: 'Document Modules',
    addButton: 'Add Module',
    editButton: 'Edit Module',
    deleteButton: 'Delete Module'
  },

  // Fields to remove during data preparation for submission (used by BaseService)
  fieldsToRemove: [
    'createdAt', 'updatedAt', 'createdBy', 'updatedBy'
  ],

  // Export filename for data exports
  exportFileName: 'document_modules',

  // Validation function for real-time field validation (dynamically assigned)
  validateField: null // Will be set after validation module loads
};

